<template>
    <div class="api-settings-container">
        <span class="headline-xl">API Portal Settings</span>
        <div v-if="message.type" :class="
        {
            'notification': true,
            'notification-success': message.type == 'success',
        }">
            <p class="paragraph-details">{{ message.message }}</p>
        </div>
        <div class="api-settings-container-key">
            <span class="title-s">API key:</span>
            <span class="title-s" id="apiKey">{{ apiKey }}</span>
            <a @click.prevent="generateApiKeyFetch" href="#" aria-label="Generate a new API Key">Generate New API Key</a>
        </div>
        <span class="title-l">Payment Information</span>
        <form class="api-settings-container-payment">
            <div>
                <custom-select id="paymentMethods" :class="{'invalid-state-input': form.errors.payment_method}" label="Select Payment Method *" :options="formattedPaymentMethods" v-model="paymentMethod"/>
                <div class="field-state input-folder"><p class="input-support-text invalid-state" v-if="form.errors.payment_method">{{ form.errors.payment_method }}</p></div>
            </div>
            <div v-show="isCreditCardPaymentMethod" class="select-wrapper" id="creditCards">
                <details @blur="creditCardSelect.setAttribute('open', false)" :tabindex="0" id="payment-card" :class="{'custom-select-wrapper': true, 'invalid-state-input': form.errors.credit_card != null}" ref=creditCardSelect @focus="creditCardSelect.setAttribute('open', true)">
                    <summary tabindex="-1">
                        <div class="custom-select-label">
                            <p id="payment-card-label" class="label">Please Select Card *</p>
                            <p class="selected-option" v-if="creditCard">
                                <component :is="creditCard.cc_type == 'Visa' ? VisaIcon : MastercardIcon" /> XXXX XXXX XXXX {{ creditCard.cc_last_4 }}
                            </p>
                        </div>
                    </summary>
                    <ul class="custom-select-options" 
                        role="listbox" 
                        aria-labelledby="payment-card-label"
                        aria-activedescendant=""
                    >
                        <li @keydown.down.prevent="traverseOptions(i+1)" @keydown.up.prevent="traverseOptions(i-1)" @keydown.enter.prevent="selectCreditCard(cc)" @click="selectCreditCard(cc)"  class="custom-select-option" v-for="(cc, i) in creditCards" :key="i">
                            <component :is="cc.cc_type == 'Visa' ? VisaIcon : MastercardIcon" /> XXXX XXXX XXXX {{ cc.cc_last_4 }}</li>
                    </ul>
                </details>
                <div class="field-state input-folder"><p class="input-support-text invalid-state" v-if="form.errors.credit_card">{{ form.errors.credit_card }}</p></div>
            </div>
        </form>
        <div class="api-settings-container-notification">
            <p class="paragraph-details">IMPORTANT: Your integration must be reviewed and tested by Rothco before Live
                Mode can be enabled. No orders will be billed or shipped while the dropship API is set to Sandbox. Once
                you have completed your integration and would like to have it tested please email
                <a href="mailto:webteam@rothco.com">webteam@rothco.com</a>
            </p>
        </div>
        <form class="api-settings-container-mode filter-content" @submit.prevent="submit()">
            <div class="filter-options">
                <div class="radio-options">
                    <label for="sandbox-mode">
                        <input v-model="form.is_sandbox" :value="true" type="radio" id="sandbox-mode" name="settings-mode" aria-label="Select Sandbox Mode" class="paragraph-details">Sandbox
                        Mode
                    </label>
                    <label for="live-mode">
                        <input  v-model="form.is_sandbox" :value="false" type="radio" id="live-mode" name="settings-mode" aria-label="Select Live Mode" class="paragraph-details">Live Mode
                    </label>
                </div>
                <div class="api-settings-container-mode-api-terms">
                    <label for="api-terms" class="flex-row-forced">
                        <input v-model="form.api_accept_terms" type="checkbox" id="api-terms" aria-label="Accept API Terms">Accept our API Terms
                    </label>
                    <div class="field-state input-folder"><p class="input-support-text invalid-state" v-if="form.errors.api_accept_terms">{{ form.errors.api_accept_terms }}</p></div>
                </div>
            </div>
            <RothcoCta type="submit" :disabled="form.processing" id="saveApiSettings" class="primary" :inertia="false">
                Save Settings
            </RothcoCta>

        </form>
    </div>
</template>
<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import VisaIcon from '@icons/visa.svg';
import MastercardIcon from '@icons/mastercard.svg';
import { useFetch } from '@/helpers/composables/useFetch';
import { route } from 'ziggy-js';
import { useForm } from '@/helpers/composables/useForm';
import { object, string, boolean } from 'yup';
import RothcoCta from '@/components/RothcoCta.vue';
import NProgress from 'nprogress';
import { onClickOutside } from '@vueuse/core';
const props = defineProps({
    api_portal_settings: {
        type: Object,
        default: () => ({})
    },
});


let message = computed(() => {
    return usePage()?.props?.flash?.message ?? {
        type: null
    };
})
let paymentMethods = computed(()=>{
    return props?.api_portal_settings?.paymentMethods ?? [];
})

let apiKey = ref(props?.api_portal_settings?.apiKey?.key ?? '');
let form = useForm({
    useInertia: true,
    schema: object({
        is_sandbox: boolean().required().default(props?.api_portal_settings?.portal_setting?.is_sandbox == 1 ? true : false),
        payment_method: string().required('Payment Method is required')
        .test(
            'is-valid-payment-method',
            'Payment method is not valid',
            value => Object.keys(paymentMethods.value).find(method => method == value)
        ).default(props?.api_portal_settings?.portal_setting?.payment_method ?? Object.keys(paymentMethods.value).find(method => paymentMethods.value[method].default == true)),
        credit_card: string().optional().test(
            'credit-card-required',
            'Credit card is required',
            value => form.payment_method == 'MV' ? value != '' : true
        ).test(
            'is-valid-credit-card',
            'Credit card is not valid',
            value => form.payment_method == 'MV' ?  creditCards.value.findIndex(card => card.credit_card_id == value) > -1 : true
        ).default(props?.api_portal_settings?.portal_setting?.credit_card_id ?? ''),
        api_accept_terms: boolean().required().oneOf([true], 'You must accept our API Terms').default(false),
    }),
})

let selectedOptions = ref([]);


let formattedPaymentMethods = computed(()=>{
    return Object.keys(paymentMethods.value).map(method => {
        return {
            key: method,
            value: paymentMethods.value[method].name
        }
    });
})

let paymentMethod = computed({
    get: () => {
        return formattedPaymentMethods.value.find(method => method.key == form.payment_method) ?? { key: '', value: '' };
    },
    set: (value) => {
        form.payment_method = value.key;
    }
})

let creditCard = computed(()=>{
    return creditCards.value.find(card => card.credit_card_id == form.credit_card);
})


let creditCards = computed(()=>{
    if(Array.isArray(props?.api_portal_settings?.cards)){
        return props?.api_portal_settings?.cards.map(card => {
            return card;
        });
    }
    return [];
})

let isCreditCardPaymentMethod = computed(()=>{
    return form.payment_method == 'MV';
})

const generateApiKeyLoading = ref(false);
const generateApiKeyFetch = ()=>{
    if(!generateApiKeyLoading.value){
        generateApiKey();
    }
}
let { execute:generateApiKey } = useFetch(route('apikey.generate'), {
    immediate: false,
    beforeFetch: ({cancel}) => {
        if(generateApiKeyLoading.value){
            cancel();
        }
        NProgress.start();
        generateApiKeyLoading.value = true;
    },
    afterFetch: (ctx) => {
        generateApiKeyLoading.value = false
        NProgress.done();
        if(ctx.response.ok){
           apiKey.value = ctx.data.data.apiKey;
        }
    },
    onFetchError: ()=>{
        generateApiKeyLoading.value = false;
    }
}).post().json();


let setField = (field, value) => {
    form[field] = value;
}


let submit = async () => {
    if(form.processing) return;
    
    await form.validate({ all: true });
    const errorsSnapshot = JSON.parse(JSON.stringify(form.errors));
    if(form.credit_card == '') delete form.credit_card;
    if (!Object.keys(errorsSnapshot).length) {
        form.transform((data) => {
            data.api_accept_terms = data.api_accept_terms ? 1 : 0;
            return data;
        }).post(route('account.apiPortal.post'));
    }
};

let creditCardSelect = ref(null);

onClickOutside(creditCardSelect, () => {
    creditCardSelect.value.removeAttribute('open');
})

function selectCreditCard(card){
    setField('credit_card', card.credit_card_id)
    creditCardSelect.value.removeAttribute('open');
}

// make traverse options and handle select for onkeydown and on enter

function traverseOptions(index){
    let currentIndex = selectedOptions.value.findIndex(option => option.key == paymentMethod.value.key);
    if(index < 0){
        index = formattedPaymentMethods.value.length - 1;
    }
    if(index >= formattedPaymentMethods.value.length){
        index = 0;
    }
    setField('credit_card', creditCards.value[index].credit_card_id);
}
</script>